import type {
  OrderDetail as PanelOrder,
  OrderDetailItem as PanelOrderItem,
  OrderDetailStatus as PanelStatus,
} from '@engyalo/delivery-ui-components'
import type { IOrderDetail } from '../graphQL/queries/order/types'
import type { OrderDetailStatus as SliceStatus } from '../redux/slices/orders/types'
import type { RootState } from '../redux/store'
import { isHidePricesEnabled } from './hidePrices'
import {
  deriveOrderOutcome,
  isOrderPending,
  resolveOrderConfirmation,
} from './orderConfirmation'

/**
 * The slice statuses that are also panel statuses, as a total map.
 *
 * Typed both ways on purpose. The key type is the slice's own union, so
 * dropping or adding a member there fails the build here rather than silently
 * falling through; the value type is the library's, so a status the panel does
 * not publish cannot be sent to it.
 *
 * `idle` is the member that makes this a map rather than a cast. It is not a
 * `OrderDetailStatus` member, and `OrderDetailPanel` defaults an unrecognised
 * `status` to `loading` — so an unmapped `idle` is not a type error at the
 * call site, it is a skeleton that never resolves, on the screen a shopper
 * just opened. Mapping it to `loading` is the same picture with an end to it.
 *
 * `pending` and `timeout` are absent because no slice status carries them:
 * both are derived below.
 * @internal
 */
const PANEL_STATUS_BY_SLICE_STATUS: Record<SliceStatus, PanelStatus> = {
  idle: 'loading',
  loading: 'loading',
  ready: 'ready',
  error: 'error',
  notFound: 'notFound',
  sessionEnded: 'sessionEnded',
}

/**
 * The panel status for the order currently on the Detail screen.
 *
 * The single place the derivation lives. Everything outside `ready` is a
 * lookup; `ready` is where the two states the slice does not name are worked
 * out from the order and the storefront's confirmation config.
 *
 * **`pending` and `timeout` only exist for an async storefront.** They
 * describe a confirmation the storefront is waiting on, so where nothing
 * confirms asynchronously (`orderConfirmation` absent, or `required` anything
 * but `true`) the order renders as soon as it resolves and neither state is
 * reachable. That is the sync path, and it is the default.
 *
 * **The client giving up and the server reporting `TIMEOUT` are different
 * facts, deliberately collapsed here.** `pollTimedOut` is our own deadline;
 * `externalRefStatus: 'TIMEOUT'` is the downstream system's verdict, and
 * either can happen without the other. The panel publishes one `timeout` slot
 * and one message for it, so the distinction has nowhere to land in the UI —
 * do not reintroduce it here on the strength of the two fields existing.
 * Datadog and the slice still carry which one occurred.
 */
export const selectOrderDetailPanelStatus = (state: RootState): PanelStatus => {
  const { selectedStatus, selected, pollTimedOut } = state.ordersSlice
  const status = PANEL_STATUS_BY_SLICE_STATUS[selectedStatus]

  if (status !== 'ready') return status

  const orderConfirmation =
    state.defaultSlice.sessionData?.configuration?.orderConfirmation
  const { isAsync } = resolveOrderConfirmation(orderConfirmation)
  if (!isAsync) return 'ready'

  if (pollTimedOut || selected?.externalRefStatus === 'TIMEOUT') {
    return 'timeout'
  }
  /*
   * Pending means the order is *both* pending by the predicate and has not
   * reached a status the storefront calls terminal. Both halves are load-bearing.
   *
   * The predicate half is read off the order, not off `isPollingOrder`: that
   * flag is true only while a tick is in flight, so between ticks a
   * still-pending order would flip back to `ready` and the confirming copy
   * would blink.
   *
   * The outcome half is what stops `externalRefStatus: PROCESSING` outranking
   * a status that settled. `createOrderAddon` sets `PROCESSING`
   * unconditionally and only the callback handler ever clears it, while the
   * native `confirmOrder` sets `status: CONFIRMED` without touching it — so on
   * an add-on storefront a confirmed order carries both, and the predicate
   * alone renders it as "confirming" for as long as the shopper looks at it.
   *
   * `deriveOrderOutcome` is not used on its own because its own last check
   * reads a bare `CREATED` order on an async storefront as pending. That is
   * the right answer for the *flow*, which is deciding whether to keep
   * polling, and the wrong one here: this screen is often opened long after
   * the order was placed, and a `CREATED` order nothing is going to move must
   * not show a confirming spinner forever.
   */
  const settled = deriveOrderOutcome(selected, orderConfirmation) !== 'pending'
  if (isOrderPending(selected) && !settled) return 'pending'

  return 'ready'
}

/** `null` reads as "the server sent nothing here", which the panel spells `undefined`. */
const optional = <T>(value: T | null | undefined): T | undefined =>
  value ?? undefined

/**
 * Total discount, summed from `Order.discountsByPromotion`.
 *
 * The map is `JSON` on the schema and keyed by promotion id, so its leaves are
 * unknown until read. Non-numeric ones are skipped rather than coerced: a
 * single null would make the sum `NaN`, and `Price`'s `hideOnZero` compares
 * against `0`, so a `NaN` renders as a discount row reading nothing.
 *
 * An empty or absent map yields `undefined`, not `0`, so the row does not draw
 * at all for orders that had no discounts.
 */
const sumDiscounts = (
  discountsByPromotion?: Record<string, unknown> | null,
): number | undefined => {
  if (!discountsByPromotion) return undefined
  const amounts = Object.values(discountsByPromotion).filter(
    (amount): amount is number =>
      typeof amount === 'number' && Number.isFinite(amount),
  )
  if (!amounts.length) return undefined
  return amounts.reduce((total, amount) => total + amount, 0)
}

/**
 * One order line, in the panel's shape.
 *
 * `priceTotal` is derived here because `CartProduct` carries no such field and
 * the panel renders `priceTotal ?? price` — without it, a line ordered twice
 * shows the unit price beside a quantity of two.
 *
 * The line's own `discounts`, `unitPrice`, `packageType` and `description` are
 * dropped rather than passed through. The panel declares none of them, so they
 * would render nowhere; and `discounts` is money, which is not worth handing
 * to a component that cannot show it.
 */
const toPanelItem = (item: {
  sku: string
  quantity: number
  name?: string | null
  price?: number | null
  imageURL?: string[] | null
  customFields?: Record<string, unknown> | null
}): PanelOrderItem => {
  const { sku, quantity, name, price, imageURL, customFields } = item
  const hasLineTotal =
    typeof price === 'number' &&
    Number.isFinite(price) &&
    Number.isFinite(quantity)

  return {
    sku,
    quantity,
    name: optional(name),
    price: optional(price),
    priceTotal: hasLineTotal ? price * quantity : undefined,
    imageURL: optional(imageURL),
    customFields: optional(customFields),
  }
}

/**
 * The `order` query's payload in the panel's shape.
 *
 * Two fields are computed rather than copied — `discounts` and each line's
 * `priceTotal` — because the library documents both as derived client-side and
 * cinnamon is the client. Everything else is a rename-free copy with the
 * schema's nulls flattened to `undefined`.
 *
 * `subtotal` is absent because `Order` has no such field yet (COM-739); the
 * panel drops the row rather than rendering an empty one.
 *
 * `discountsByPromotion` and `compensations` are deliberately **not** passed
 * through. The first is money under arbitrary promotion-id keys, which
 * `maskPricesForDisplay` cannot recognise and so would hand unmasked to a
 * hide-prices shopper; the second the panel does not render. Neither is worth
 * carrying for a component that would only ignore it.
 */
export const toOrderDetailPanelOrder = (
  order?: IOrderDetail | null,
): PanelOrder | undefined => {
  if (!order) return undefined

  return {
    id: order.id,
    sequence: optional(order.sequence),
    status: optional(order.status),
    createdAt: optional(order.createdAt),
    expectedDeliveryDate: optional(order.expectedDeliveryDate),
    paymentMethod: optional(order.paymentMethod),
    customer: optional(order.customer)
      ? {
          address: optional(order.customer?.address),
          city: optional(order.customer?.city),
          state: optional(order.customer?.state),
          country: optional(order.customer?.country),
          zipCode: optional(order.customer?.zipCode),
          region: optional(order.customer?.region),
        }
      : undefined,
    items: Array.isArray(order.items)
      ? order.items.filter(Boolean).map(toPanelItem)
      : undefined,
    total: optional(order.total),
    totalFee: optional(order.totalFee),
    activeFees: optional(order.activeFees),
    discounts: sumDiscounts(order.discountsByPromotion),
    customFields: optional(order.customFields),
  }
}

/**
 * The `OrderDetailPanel` props cinnamon owns rather than the config author.
 *
 * Injected in `useCinnamonProps` for any component whose `importRoute` is
 * `OrderDetailPanel`, the way the `MinAmount` progress message already is.
 * These three cannot be authored as `{{variables}}`: two are derived from
 * state the config DSL cannot compute, and the third is a shape the DSL cannot
 * build.
 *
 * Everything else on the panel — labels, `messages`, `statusMap`, `currency`,
 * `fieldsToDisplay`, the custom slots and the actions — stays in config. This
 * adds no layout decision to cinnamon.
 *
 * The values are injected **before** `processData` and `maskPricesForDisplay`
 * run, so the order goes through masking with everything else. Injecting
 * afterwards would hand a hide-prices shopper real totals.
 */
export const getOrderDetailPanelProps = (state: RootState) => ({
  order: toOrderDetailPanelOrder(state.ordersSlice.selected),
  status: selectOrderDetailPanelStatus(state),
  /**
   * Slot text is a string, and `maskPricesForDisplay` is an allowlist of prop
   * names over numbers, so it cannot see a price inside `'Precio por kilo
   * $98.90'`. This is what the panel needs to suppress those; the numbers are
   * already covered by masking.
   */
  hidePrices: isHidePricesEnabled(state),
})

export default getOrderDetailPanelProps
